import * as React from "react";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient, useMutation } from "react-query";

import { Risk } from "./types";
import { useClient } from "utils/client";
import { ControlContext } from "context/Controls";
import { DeleteButton } from "components/Button/DeleteButton";
import { createRiskTemplate } from "components/RiskTemplates/CreateRiskTemplateOnDashboard";
import { useCreateRisk } from "./CreateRiskOnDashboard";

interface OptionsProps {
  onClose: () => void;
  onDelete: () => void;
  projectCRN: string;
  risk: Risk;
}

export const useCreateTemplateFromRisk = () => {
  const client = useClient();
  const { t } = useTranslation();
  const { displayNotification } = useContext(ControlContext);

  const queryClient = useQueryClient();

  const { mutate, isLoading } = useMutation(
    async (
      payload: Risk | { blueprint_name: string; blueprint_description: string }
    ) => await createRiskTemplate(client, payload),
    {
      onSuccess: async (riskTemplate) => {
        await queryClient.refetchQueries(["riskTemplates"]);

        displayNotification!(
          t("riskTemplate.create.save_success"),
          t("riskTemplate.create.save_success_subline", {
            templateName: riskTemplate.blueprint_name,
          })
        );
      },
    }
  );

  return { createRiskTemplate: mutate, isLoading };
};

export const Options: React.FunctionComponent<OptionsProps> = (props) => {
  const { t } = useTranslation();
  const { createRiskTemplate } = useCreateTemplateFromRisk();
  const { createRisk } = useCreateRisk(props.projectCRN);

  const handleDelete = () => {
    props.onDelete();
    props.onClose();
  };

  return (
    <div
      className="mx-3 origin-top-right absolute right-7 top-0 w-64 mt-1 rounded-md shadow-lg z-10 bg-white ring-1 ring-black ring-opacity-5 divide-y divide-gray-200 focus:outline-none"
      role="menu"
      aria-orientation="vertical"
      aria-labelledby="project-options-menu-0"
    >
      <div className="py-1" role="none">
        <button
          onClick={() => {
            createRiskTemplate({
              ...props.risk,
              blueprint_name:
                props.risk.readable_name ||
                t("riskTemplate.create.copy_from_risk_alternate_name"),
              blueprint_description: props.risk.description,
            });
            props.onClose();
          }}
          className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 w-full text-left flex items-center"
          role="menuitem"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="text-gray-500 mr-3 h-6 w-6 flex-shrink-0 mt-0.5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25"
            />
          </svg>
          {t("riskTemplate.create.copy_from_risk")}
        </button>
        <button
          onClick={() => {
            createRisk({
              template: {
                ...props.risk,
                resource_id: "",
                readable_name: props.risk.readable_name + " (Copy)",
              },
            });
            props.onClose();
          }}
          className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100 w-full text-left flex items-center"
          role="menuitem"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="text-gray-500 mr-3 h-6 w-6 flex-shrink-0 mt-0.5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75"
            />
          </svg>

          {t("riskTemplate.create.duplicate")}
        </button>
      </div>
      <div className="py-1" role="none">
        <DeleteButton onClick={handleDelete} flat fullWidth>
          {t("form.mitigations.delete_risk")}
        </DeleteButton>
      </div>
    </div>
  );
};
